import { Col, Form, Row } from 'antd';
import KlwSubHeader from 'components/headers/klw-sub-header';
import { GetKotoBeheerApplicatieInstellingen, KotoDataModel } from 'pages/beheer/dataproducten/models/kotoModel';
import { useCallback, useEffect, useState } from 'react';
import { useRefreshMenu } from '../../../helpers/functions';
import { useKlwReadOnly } from '../../../helpers/hooks';
import { errorHandler, succesHandler } from '../../../helpers/notification';
import { ArealenFosfaatGrondsoorten } from './arealen-fosfaat-grondsoorten';
import { BodemkoolstofInvoerwaardes } from './bodemkoolstof-invoerwaardes';
import { GewasPercelen } from './gewaspercelen/gewaspercelen';
import { HandmatigeGewasPercelen } from './handmatige-gewaspercelen/handmatige-gewaspercelen';
import { flattenState, mapToLayout, revertMapFromFlattenState } from './mapper';
import { herberekenPerceelGegevens } from './models/herberekenMock';
import { getBodemPercelen, PercelenModel, postBodemPercelen } from './models/percelenMock';
import { Switches } from './switches';
import { TeeltenVanggewassen } from './teelten-vanggewassen/teelten-vanggewassen';

const PercelenPage = () => {
  //#region
  const isKlwReadOnly = useKlwReadOnly();
  const [isLoading, setLoading] = useState(true);
  const [isSaving, setSaving] = useState(false);
  const [isRefreshing, setRefreshing] = useState(false);
  const [isFormChanged, setFormChanged] = useState(false);
  const [kotoData, setKotoData] = useState<KotoDataModel>({} as KotoDataModel);
  const klwJaar = Number(localStorage.getItem('klwJaar'));
  const klwPilotId = Number(localStorage.getItem('klwPilotId'));

  const [percelenModel, setPercelenModel] = useState<PercelenModel>({} as PercelenModel);
  const { refreshMenu } = useRefreshMenu();

  const [form] = Form.useForm<PercelenModel>();
  const [arealenFosfaatGrondsoortenForm] = Form.useForm();
  const isPilotBodemKoolstof = klwPilotId === 1;

  useEffect(() => {
    (async () => {
      try {
        const bodemPercelenResult = await getBodemPercelen();
        setPercelenModel(bodemPercelenResult);
        form.setFieldsValue(bodemPercelenResult);
        arealenFosfaatGrondsoortenForm.setFieldsValue({
          grond: {
            fosfaattoestandOppervlakten: bodemPercelenResult.grond.fosfaattoestandOppervlakten,
          },
          grondsoorten: {
            verdelingGrondsoorten: bodemPercelenResult.grondsoorten.verdelingGrondsoorten,
          },
          kenmerkenMineralen: {
            kenmerkenMineralenGrondToestanden: bodemPercelenResult.kenmerkenMineralen?.kenmerkenMineralenGrondToestanden,
          },
        });

        const kotoDataResult = await GetKotoBeheerApplicatieInstellingen();
        setKotoData(kotoDataResult);
      } catch (error) {
        errorHandler('Fout bij ophalen van bodem perceel gegevens', error);
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKlwReadOnly]);

  const herbereken = useCallback(
    async (
      gebruikKoppeltool?: boolean,
      gebruikKoppeltoolKoolstofBodem?: boolean,
      gebruikKoppeltoolOud?: boolean,
      gebruikKoppeltoolKoolstofBodemOud?: boolean
    ) => {
      setRefreshing(true);
      const { gekoppeldeGewaspercelen, gewaspercelen } = await form.validateFields();
      if ((gewaspercelen && gewaspercelen.length) || (gekoppeldeGewaspercelen && gekoppeldeGewaspercelen.length)) {
        setFormChanged(false);

        herberekenPerceelGegevens({
          gekoppeldeGewaspercelen: gekoppeldeGewaspercelen,
          gewaspercelen: gewaspercelen,
          gebruikKoppeltool: gebruikKoppeltool,
          gebruikKoppeltoolKoolstofBodem: gebruikKoppeltoolKoolstofBodem,
        })
          .then(response => {
            const updateFosfaatWithResponse = gebruikKoppeltool || gebruikKoppeltoolOud;
            const updateGrondsoortWithResponse =
              gebruikKoppeltool || gebruikKoppeltoolOud || gebruikKoppeltoolKoolstofBodem || gebruikKoppeltoolKoolstofBodemOud;

            const { mappedFosfaatValues, mappedGrondsoorten } = revertMapFromFlattenState(arealenFosfaatGrondsoortenForm.getFieldsValue());
            const previousFosfaatFromForm = mappedFosfaatValues as any;
            const previousGrondsoortFromForm = mappedGrondsoorten as any;

            const filteredVerdelingGrondsoorten = (
              updateGrondsoortWithResponse ? response.grondsoortenModel.verdelingGrondsoorten : previousGrondsoortFromForm
            ).filter(f => f.grondsoortcategorieId !== 6);

            const arealenFosfaatGrondsoorten = mapToLayout(
              updateFosfaatWithResponse ? response.grondfosfaatToestandenModel.fosfaattoestandOppervlakten : previousFosfaatFromForm,
              filteredVerdelingGrondsoorten
            );
            const arealenFosfaatGrondsoortenObject = flattenState(arealenFosfaatGrondsoorten);

            arealenFosfaatGrondsoortenForm.setFieldsValue(arealenFosfaatGrondsoortenObject);

            arealenFosfaatGrondsoortenForm.setFieldsValue({
              grond: {
                fosfaattoestandOppervlakten: updateFosfaatWithResponse
                  ? response.grondfosfaatToestandenModel.fosfaattoestandOppervlakten
                  : previousFosfaatFromForm,
              },
              grondsoorten: {
                verdelingGrondsoorten: updateGrondsoortWithResponse ? response.grondsoortenModel.verdelingGrondsoorten : previousGrondsoortFromForm,
              },
              kenmerkenMineralen: {
                kenmerkenMineralenGrondToestanden: response.kenmerkenMineralenGrondToestandenModel?.kenmerkenMineralenGrondToestanden,
              },
            });

            setPercelenModel(current => {
              return {
                ...current,
                grond: {
                  fosfaattoestandOppervlakten: updateFosfaatWithResponse
                    ? previousFosfaatFromForm
                    : response.grondfosfaatToestandenModel.fosfaattoestandOppervlakten,
                },
                grondsoorten: {
                  verdelingGrondsoorten: updateGrondsoortWithResponse ? previousGrondsoortFromForm : response.grondsoortenModel.verdelingGrondsoorten,
                },
                kenmerkenMineralen: {
                  kenmerkenMineralenGrondToestanden: response.kenmerkenMineralenGrondToestandenModel?.kenmerkenMineralenGrondToestanden,
                },
              };
            });
          })
          .catch(error => {
            errorHandler('Fout bij ophalen herberekende perceel gegevens', error);
            console.error(error);
          })
          .finally(async () => {
            setRefreshing(false);
          });
      }
      setRefreshing(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [form]
  );

  const onFinish = async (data: PercelenModel) => {
    setSaving(true);
    const { gekoppeldeGewaspercelen, voorNaTeeltVanggewassen, gewaspercelen, gebruikKoppeltool } = await form.validateFields();
    const arealenFosfaat = await arealenFosfaatGrondsoortenForm.validateFields();
    const mappedGekoppeldeGewaspercelen = gekoppeldeGewaspercelen?.map(x => {
      return {
        ...x,
        fixerend: x.fosfaatklasseBepalingMethode ? true : x.fixerend,
      };
    });
    const mappedGewaspercelen = gewaspercelen?.map(x => {
      return {
        ...x,
        fixerend: x.fosfaatklasseBepalingMethode ? true : x.fixerend,
      };
    });
    const { mappedFosfaatValues, mappedGrondsoorten } = revertMapFromFlattenState(arealenFosfaat);
    data.gekoppeldeGewaspercelen = mappedGekoppeldeGewaspercelen;
    data.gewaspercelen = mappedGewaspercelen?.filter(x => !x.handmatig || (x.handmatig && x.perceelNaam));
    data.gebruikKoppeltool = gebruikKoppeltool;
    data.voorNaTeeltVanggewassen = voorNaTeeltVanggewassen;
    (data.grond as any) = { fosfaattoestandOppervlakten: mappedFosfaatValues };
    (data.grondsoorten as any) = { verdelingGrondsoorten: mappedGrondsoorten };
    let mutatedVoorNaTeeltVanggewassen;
    if (data.voorNaTeeltVanggewassen) {
      Object.keys(data.voorNaTeeltVanggewassen).forEach(key => {
        mutatedVoorNaTeeltVanggewassen = {
          ...mutatedVoorNaTeeltVanggewassen,
          [key]: data.voorNaTeeltVanggewassen[key]?.map(x => {
            return {
              ...x,
              isOpgegeven: data.voorNaTeeltVanggewassen[key][0].isOpgegeven,
            };
          }),
        };
      });
      data.voorNaTeeltVanggewassen = mutatedVoorNaTeeltVanggewassen;
    }
    const model = { ...percelenModel, ...data };

    setLoading(true);
    postBodemPercelen(model)
      .then(r => {
        setPercelenModel(r);
        form.setFieldsValue(r);
        succesHandler('Succesvol opgeslagen', 'Perceelgegevens zijn opgeslagen');
      })
      .catch(error => {
        console.error(error);
        errorHandler('Fout bij opslaan gegevens', error);
      })
      .finally(() => {
        refreshMenu();
        setLoading(false);
        setSaving(false);
      });
  };

  return (
    <Form form={form} onValuesChange={() => setFormChanged(true)} onFinish={onFinish}>
      <KlwSubHeader form={form} isKlwReadOnly={isKlwReadOnly} isSaving={isSaving} title="Percelen" />
      <Row>
        <Col span={24}>
          <Switches
            kotoData={kotoData}
            gebruikKoppeltoolKoolstofBodem={isPilotBodemKoolstof}
            isKlwReadOnly={isKlwReadOnly}
            isLoading={isLoading}
            klwJaar={klwJaar}
          />
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => {
              return prev.gebruikKoppeltool !== cur.gebruikKoppeltool || prev.gebruikVanggewassen !== cur.gebruikVanggewassen;
            }}>
            {({ getFieldValue }) => {
              const gebruikKoppeltool: PercelenModel['gebruikKoppeltool'] = getFieldValue(['gebruikKoppeltool']);
              const gebruikKoppeltoolKoolstofBodem: PercelenModel['gebruikKoppeltoolKoolstofBodem'] = getFieldValue(['gebruikKoppeltoolKoolstofBodem']);
              const gebruikVanggewassen: PercelenModel['gebruikVanggewassen'] = getFieldValue(['gebruikVanggewassen']);
              const gebruikKoppeltoolKoolstofBodemAndPilotActive = gebruikKoppeltoolKoolstofBodem && isPilotBodemKoolstof;
              return (
                <>
                  {(gebruikKoppeltool || (klwJaar >= 2023 && gebruikKoppeltoolKoolstofBodemAndPilotActive)) && (
                    <>
                      <GewasPercelen
                        gebruikKoppeltoolKoolstofBodem={gebruikKoppeltoolKoolstofBodemAndPilotActive && klwJaar >= 2023}
                        gebruikKoppeltool={gebruikKoppeltool}
                        isKlwReadOnly={isKlwReadOnly}
                        isLoading={isLoading}
                      />
                      <HandmatigeGewasPercelen
                        isKlwReadOnly={isKlwReadOnly}
                        isLoading={isLoading}
                        klwJaar={klwJaar}
                        gebruikKoppeltool={gebruikKoppeltool}
                        gebruikKoppeltoolKoolstofBodem={gebruikKoppeltoolKoolstofBodemAndPilotActive && klwJaar >= 2023}
                      />
                    </>
                  )}
                  <ArealenFosfaatGrondsoorten
                    isEditable={!gebruikKoppeltool}
                    isRefreshing={isRefreshing}
                    isKlwReadOnly={isKlwReadOnly}
                    isFormChanged={isFormChanged}
                    form={arealenFosfaatGrondsoortenForm}
                    isLoading={isLoading}
                    herbereken={() => herbereken()}
                    klwJaar={klwJaar}
                    bothKotoSettingsOff={!gebruikKoppeltool && !gebruikKoppeltoolKoolstofBodem}
                  />
                  {klwJaar >= 2023 && percelenModel && percelenModel?.kenmerkenMineralen && isPilotBodemKoolstof && (
                    <BodemkoolstofInvoerwaardes
                      isEditable={!gebruikKoppeltoolKoolstofBodem}
                      isRefreshing={isRefreshing}
                      isKlwReadOnly={isKlwReadOnly}
                      isFormChanged={isFormChanged}
                      percelenModel={percelenModel}
                      isLoading={isLoading}
                      klwJaar={klwJaar}
                      herbereken={() => herbereken()}
                    />
                  )}
                  {gebruikVanggewassen && <TeeltenVanggewassen isKlwReadOnly={isKlwReadOnly} isLoading={isLoading} />}
                </>
              );
            }}
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default PercelenPage;
